import type React from 'react';
import { useEffect } from 'react';
import {
  Phone, Video, CalendarCheck, MapPin, Mail, ClipboardList, CalendarClock,
  Stethoscope, HeartPulse, Bell, Repeat, Star, MessageCircle, Wrench, Users,
} from 'lucide-react';
import { useActivityTypesStore, SYSTEM_ACTIVITY_TYPES } from '../../../lib/stores/activityTypesStore';

export interface ActivityTypeOption {
  value: string;
  label: string;
  icon: any;
}

// Icona per i tipi di sistema — stessa mappatura di getTypeIcon in DealActivityForm.tsx
// (fallback CalendarClock per 'other', identico al ramo default della fonte).
const SYSTEM_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  call: Phone,
  videochiamata: Video,
  appuntamento: CalendarCheck,
  sopralluogo: MapPin,
  email: Mail,
  task: ClipboardList,
};

// Icona per i tipi custom — stessa mappatura di ICON_COMPONENTS in
// src/components/settings/CustomActivityTypes.tsx (non esportata dal file sorgente,
// replicata qui per evitare un import UI→hook attraverso Settings; stesso fallback
// ClipboardList di ActivityTypeIcon per chiavi icona sconosciute).
const CUSTOM_TYPE_ICONS_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'clipboard-list': ClipboardList,
  'stethoscope': Stethoscope,
  'heart-pulse': HeartPulse,
  'bell': Bell,
  'repeat': Repeat,
  'star': Star,
  'calendar-check': CalendarCheck,
  'message-circle': MessageCircle,
  'wrench': Wrench,
  'users': Users,
};

/**
 * Opzioni per la select "Tipo" del form attività: tipi standard di sistema + tipi
 * custom per-org attivi da useActivityTypesStore, in coda — stesso pattern della
 * select "Tipo" in LeadStatusUpdateDrawer.tsx:1958-2000 (fetch dei custom types
 * su mount se non già caricati, filtro `active`, ordine sistema→custom).
 *
 * DEVIAZIONE dalla fonte: la select nativa della fonte non mostra icone (solo
 * `<option>` testuali); il campo `icon` è richiesto dal contratto del drawer
 * condiviso per una futura select custom con icone per riga. Standard → mappatura
 * di DealActivityForm.tsx:1081-1094 (`getTypeIcon`). Custom → mappatura di
 * CustomActivityTypes.tsx:15-26 (`ICON_COMPONENTS`, dietro `ActivityTypeIcon`),
 * risolta qui su `ct.icon` invece che renderizzata, per restare un dato puro.
 *
 * Non replica la logica "mantieni in coda il tipo custom disattivato selezionato"
 * di LeadStatusUpdateDrawer.tsx:238-245: qui non c'è un valore corrente da
 * confrontare (l'hook non prende parametri) — resta a carico del consumer.
 */
export function useActivityTypeOptions(): ActivityTypeOption[] {
  const { customTypes, fetched: customTypesFetched, fetchCustomTypes } = useActivityTypesStore();

  useEffect(() => {
    if (!customTypesFetched) fetchCustomTypes();
  }, [customTypesFetched, fetchCustomTypes]);

  const standardOptions: ActivityTypeOption[] = SYSTEM_ACTIVITY_TYPES.map(t => ({
    value: t.value,
    label: t.label,
    icon: SYSTEM_TYPE_ICONS[t.value] || CalendarClock,
  }));

  const customOptions: ActivityTypeOption[] = customTypes
    .filter(ct => ct.active)
    .map(ct => ({
      value: ct.slug,
      label: ct.name,
      icon: CUSTOM_TYPE_ICONS_MAP[ct.icon] || ClipboardList,
    }));

  return [...standardOptions, ...customOptions];
}
